import { toast } from '@heroui/react';
import { useState, useMemo } from 'react';

import type { SortingState, PaginationState } from '@hs/ui';
import { AlertDialog, Button, Chip, Dropdown, Label } from '@heroui/react';
import {
  IconPlus as Plus,
  IconRefresh as RefreshCw,
  IconDotsVertical as MoreVertical,
} from '@tabler/icons-react';
import { trpc } from '@/lib/trpc';
import { useAppSelector } from '@/stores/hooks';
import {
  PageDataGrid,
  PageDataGridActionColumn,
  PageDataGridActionToolbar,
} from '@/components/layouts/page/PageDataGrid';
import { AiModelEditorModal } from './AiModelEditorModal';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';
import { AI_MODEL_TYPE_LABELS, type AiModelType } from '../constants';

type AiModel = {
  id: string;
  providerId?: string | null;
  modelCode?: string | null;
  modelVersion?: string | null;
  modelName?: string | null;
  modelType?: string | null;
  contextLength?: number | null;
  maxOutputTokens?: number | null;
  isMultimodal?: boolean | null;
  isEnabled?: boolean | null;
  createdAt: string;
};

export function AiModelsManagement() {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });
  const [editorModalOpen, setEditorModalOpen] = useState(false);
  const [editingModel, setEditingModel] = useState<AiModel | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [modelToDelete, setModelToDelete] = useState<AiModel | null>(null);
  const [deletingModelId, setDeletingModelId] = useState<string | null>(null);

  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const isHydrated = useAppSelector((state) => state.auth.isHydrated);
  const utils = trpc.useUtils();

  const sortItem = sorting[0];

  const { data: providersData } = trpc.aiProvider.listAll.useQuery(
    {},
    { enabled: isHydrated && isAuthenticated }
  );

  const providerNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of providersData ?? []) {
      const row = p as { id?: string; name?: string | null; code?: string | null };
      if (row.id) {
        map.set(row.id, row.name || row.code || row.id);
      }
    }
    return map;
  }, [providersData]);

  const { data, isLoading, refetch } = trpc.aiModel.list.useQuery(
    {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      ...(sortItem
        ? { sortBy: sortItem.id, sortOrder: sortItem.desc ? ('desc' as const) : ('asc' as const) }
        : {}),
    },
    { enabled: isHydrated && isAuthenticated }
  );

  const models = (data?.items ?? []) as AiModel[];
  const total = data?.total ?? 0;
  const pageCount = useMemo(
    () => Math.ceil(total / (pagination.pageSize || 10)),
    [total, pagination.pageSize]
  );

  const deleteModelMutation = trpc.aiModel.delete.useMutation({
    onSuccess: () => {
      toast.success('AI 模型删除成功');
      setDeletingModelId(null);
      setDeleteModalOpen(false);
      setModelToDelete(null);
      void utils.aiModel.list.invalidate();
    },
    onSettled: () => {
      setDeletingModelId(null);
    },
  });

  const handleCreate = () => {
    setEditingModel(null);
    setEditorModalOpen(true);
  };

  const handleEdit = (model: AiModel) => {
    setEditingModel(model);
    setEditorModalOpen(true);
  };

  const handleEditorSuccess = () => {
    setEditorModalOpen(false);
    setEditingModel(null);
    void refetch();
  };

  const handleDelete = (model: AiModel) => {
    setModelToDelete(model);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!modelToDelete) return;
    setDeletingModelId(modelToDelete.id);
    try {
      await deleteModelMutation.mutateAsync({ id: modelToDelete.id });
    } catch {
      /* onError */
    }
  };

  const columnOverrides = useMemo(
    () => ({
      providerId: {
        cell: (row: AiModel) => {
          const id = row.providerId;
          if (!id) return '—';
          return providerNameMap.get(id) ?? id;
        },
      },
      modelType: {
        cell: (row: AiModel) => {
          const t = row.modelType as AiModelType | null | undefined;
          if (!t) return '—';
          return AI_MODEL_TYPE_LABELS[t] ?? t;
        },
      },
      isEnabled: {
        cell: (row: AiModel) => (
          <Chip color={row.isEnabled ? 'success' : 'default'} size="sm" variant="soft">
            <Chip.Label>{row.isEnabled ? '已启用' : '已停用'}</Chip.Label>
          </Chip>
        ),
      },
      isMultimodal: {
        cell: (row: AiModel) => (
          <Chip color={row.isMultimodal ? 'accent' : 'default'} size="sm" variant="soft">
            <Chip.Label>{row.isMultimodal ? '是' : '否'}</Chip.Label>
          </Chip>
        ),
      },
      modelVersion: {
        cell: (row: AiModel) => row.modelVersion || '—',
      },
    }),
    [providerNameMap]
  );

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <PageDataGrid<AiModel>
          className="flex min-h-0 min-w-0 flex-1 flex-col"
          source="ai_model"
          data={models}
          recordCount={total}
          pageCount={pageCount}
          isLoading={isLoading}
          emptyMessage="暂无数据"
          getRowId={(row) => row.id}
          sorting={sorting}
          onSortingChange={setSorting}
          pagination={pagination}
          onPaginationChange={setPagination}
          columnOverrides={columnOverrides}
          metaQueryOptions={{ enabled: isHydrated && isAuthenticated }}
          paginationBar={{}}
        >
          <PageDataGridActionToolbar>
            <div className="flex flex-wrap items-center gap-2">
              <Button onPress={handleCreate}>
                <Plus className="size-4" stroke={1.5} />
                创建
              </Button>
              <Button variant="secondary" onPress={() => void refetch()}>
                <RefreshCw className="size-4" stroke={1.5} />
                刷新
              </Button>
            </div>
          </PageDataGridActionToolbar>
          <PageDataGridActionColumn<AiModel>>
            {({ row }) => {
              const model = row.original;
              return (
                <div className="flex justify-center">
                  <Dropdown>
                    <Button isIconOnly aria-label="操作" size="sm" variant="ghost">
                      <MoreVertical className="size-4" stroke={1.5} />
                    </Button>
                    <Dropdown.Popover className="min-w-[140px]" placement="bottom end">
                      <Dropdown.Menu>
                        <Dropdown.Item id="edit" textValue="编辑" onAction={() => handleEdit(model)}>
                          <Label>编辑</Label>
                        </Dropdown.Item>
                        <Dropdown.Item
                          id="delete"
                          textValue="删除"
                          variant="danger"
                          onAction={() => handleDelete(model)}
                        >
                          <Label>删除</Label>
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown.Popover>
                  </Dropdown>
                </div>
              );
            }}
          </PageDataGridActionColumn>
        </PageDataGrid>
      </div>

      <AiModelEditorModal
        open={editorModalOpen}
        model={editingModel}
        onCancel={() => {
          setEditorModalOpen(false);
          setEditingModel(null);
        }}
        onSuccess={handleEditorSuccess}
      />

      <AlertDialog isOpen={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <RacDialogTriggerAnchor />
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading>确认删除</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                确定要删除 AI 模型「{modelToDelete?.modelName ?? modelToDelete?.modelCode}」吗？此操作不可恢复。
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button
                  variant="secondary"
                  onPress={() => {
                    setDeleteModalOpen(false);
                    setModelToDelete(null);
                  }}
                >
                  取消
                </Button>
                <Button
                  variant="danger"
                  isDisabled={deletingModelId === modelToDelete?.id}
                  onPress={() => void confirmDelete()}
                >
                  {deletingModelId === modelToDelete?.id ? '删除中…' : '确认删除'}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>
    </div>
  );
}
